import { zodResolver } from '@hookform/resolvers/zod';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { MaskedField } from '@components/MaskedField';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { formatCnpj, isValidCnpj } from '@functions/brazil-documents';
import { CnpjLookupService } from '@services/cnpj-lookup.service';
import { CrmPhoneField } from '../../kanban-board/components/shared/crm-phone-field';
import { onlyDigits } from '../../kanban-board/formatters/crm-phone-mask';
import { AddressFields } from '../../people/components/address-fields';
import {
    ADDRESS_FIELDS, addressSchema, addressToForm, EMPTY_ADDRESS, refineAddress,
} from '../../people/address-form';
import { CompanyService } from '../services/company.service';
import { CompanyResponse } from '../services/responses/company.response';
import { CompanyFormValues, cnpjLookupFields, inactiveRegistration, toCompanyRequest } from '../company-form';
import { firstErrorMessage } from '@functions/first-error-message';

const MIN_PHONE_DIGITS = 10;

type Section = 'dados' | 'endereco';

interface Props {
    open: boolean;
    edit: CompanyResponse | null;
    handleModal: () => void;
    getAll: () => void;
    /// Quem abriu o modal para cadastrar e já usar a empresa (o modal de lead).
    onSaved?: (company: CompanyResponse) => void;
}

export const CompaniesModal = ({ open, edit, handleModal, getAll, onSaved }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();

    const [opened, setOpened] = useState<Section[]>(['dados']);

    const toggle = (section: Section) => setOpened((current) => (
        current.includes(section) ? current.filter((item) => item !== section) : [...current, section]));

    const schema = z
        .object({
            name: z.string().trim().min(1, t('validacao.obrigatorio')).max(200),
            tradeName: z.string().max(200).optional(),
            cnpj: z
                .string()
                .optional()
                .refine((value) => !value || isValidCnpj(value), t('companies.cnpjInvalido')),
            phone: z
                .string()
                .optional()
                .refine(
                    (value) => !onlyDigits(value ?? '') || onlyDigits(value ?? '').length >= MIN_PHONE_DIGITS,
                    t('companies.telefoneIncompleto')
                ),
            email: z.string().email().max(150).optional().or(z.literal('')),
            notes: z.string().max(1000).optional(),
            ...addressSchema,
        })
        .superRefine(refineAddress(t));

    const empty: CompanyFormValues = {
        name: '', tradeName: '', cnpj: '', phone: '', email: '', notes: '', ...EMPTY_ADDRESS,
    };

    const form = useForm<CompanyFormValues>({
        resolver: zodResolver(schema),
        defaultValues: empty,
    });

    const { handleSubmit, reset, setValue, getValues, formState: { errors } } = form;

    useEffect(() => {
        if (!open) return;

        reset({
            ...empty,
            name: edit?.name ?? '',
            tradeName: edit?.tradeName ?? '',
            cnpj: edit?.cnpj ?? '',
            phone: edit?.phone ? onlyDigits(edit.phone) : '',
            email: edit?.email ?? '',
            notes: edit?.notes ?? '',
            ...addressToForm(edit?.address),
        });

        setOpened(['dados', ...(edit?.address ? ['endereco' as const] : [])]);
    }, [open, edit]);

    useEffect(() => {
        const fields = Object.keys(errors);
        if (fields.length === 0) return;

        showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);

        // Campo inválido numa seção fechada faria o salvar parecer que não funciona.
        const withError: Section[] = fields.some((field) => (ADDRESS_FIELDS as readonly string[]).includes(field))
            ? ['endereco']
            : ['dados'];
        setOpened((current) => [...new Set([...current, ...withError])]);
    }, [errors]);

    const searchCnpj = async (cnpj: string) => {
        if (!isValidCnpj(cnpj)) return;

        setLoading(dispatch, true);

        try {
            const { data } = await new CnpjLookupService().lookup(cnpj);

            const fields = cnpjLookupFields(data, {
                tradeName: getValues('tradeName'),
                postalCode: getValues('postalCode'),
            });

            Object.entries(fields).forEach(([field, value]) =>
                setValue(field as keyof CompanyFormValues, value ?? ''));

            if (fields.postalCode) setOpened((current) => [...new Set([...current, 'endereco' as const])]);

            const status = inactiveRegistration(data);
            if (status) showAlert(t('companies.cnpjSituacao', { situacao: status }), 'warning', 5000);
        } catch {
            showAlert(t('companies.cnpjNaoEncontrado'), 'warning', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const save = async (data: CompanyFormValues) => {
        setLoading(dispatch, true);
        try {
            const body = toCompanyRequest(data);
            const service = new CompanyService();

            const res = edit ? await service.update(edit.id, body) : await service.create(body);

            showAlert(edit ? t('general.atualizacao') : t('general.cadastro'), 'success', 2000);
            getAll();
            onSaved?.(res.data.data);
            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const section = (key: Section, title: string, content: React.ReactNode) => (
        <Accordion expanded={opened.includes(key)} onChange={() => toggle(key)} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>{content}</Grid>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="xl" scroll="paper">
            <DialogTitle>{edit ? t('companies.editar') : t('companies.nova')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(save)}>
                    <DialogContent style={smallDialog} sx={{ display: 'grid', gap: 1.5 }}>
                        {section('dados', t('companies.dadosDaEmpresa'), (
                            <>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('companies.cnpj')}</Form.Label>
                                        <MaskedField
                                            name="cnpj"
                                            size="small"
                                            fullWidth
                                            mask={formatCnpj}
                                            maxDigits={14}
                                            onBlurDigits={searchCnpj}
                                            placeholder="00.000.000/0000-00"
                                        />
                                        <Form.ErrorMessage field="cnpj" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={5}>
                                    <FormControl fullWidth>
                                        <Form.Label>
                                            {t('companies.razaoSocial')}
                                            <Form.MandatoryIcon />
                                        </Form.Label>
                                        <Form.TextFields name="name" size="small" inputProps={{ maxLength: 200 }} placeholder={t('companies.phRazaoSocial')} />
                                        <Form.ErrorMessage field="name" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={4}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('companies.nomeFantasia')}</Form.Label>
                                        <Form.TextFields name="tradeName" size="small" inputProps={{ maxLength: 200 }} placeholder={t('companies.phNomeFantasia')} />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={4}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.telefone')}</Form.Label>
                                        <CrmPhoneField name="phone" size="small" fullWidth placeholder={t('people.phTelefone')} />
                                        <Form.ErrorMessage field="phone" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={8}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.email')}</Form.Label>
                                        <Form.TextFields name="email" size="small" inputProps={{ maxLength: 150 }} placeholder={t('people.phEmail')} />
                                        <Form.ErrorMessage field="email" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={12}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.observacoes')}</Form.Label>
                                        <Form.TextFields
                                            name="notes"
                                            placeholder={t('companies.phObservacoes')}
                                            multiline
                                            minRows={3}
                                            size="small"
                                            inputProps={{ maxLength: 1000 }}
                                        />
                                    </FormControl>
                                </Form.Field>
                            </>
                        ))}

                        {section('endereco', t('people.endereco'), <AddressFields />)}
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={handleModal}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
